export enum AdminRole {
  SuperAdmin = 'SUPER_ADMIN',
  OperationAdmin = 'OPERATION_ADMIN',
  StoreManager = 'STORE_MANAGER',
  StoreStaff = 'STORE_STAFF',
  Finance = 'FINANCE',
  CustomerService = 'CUSTOMER_SERVICE',
}

export const Permission = {
  All: '*',
  DashboardView: 'dashboard:view',
  DashboardEdit: 'dashboard:edit',
  OrderView: 'order:view',
  OrderEdit: 'order:edit',
  RefundView: 'refund:view',
  RefundEdit: 'refund:edit',
  StoreView: 'store:view',
  StoreEdit: 'store:edit',
  ProductView: 'product:view',
  ProductEdit: 'product:edit',
  DeliveryView: 'delivery:view',
  DeliveryEdit: 'delivery:edit',
  DroneView: 'drone:view',
  DroneEdit: 'drone:edit',
  UserView: 'user:view',
  UserEdit: 'user:edit',
  MemberView: 'member:view',
  MemberEdit: 'member:edit',
  MarketingView: 'marketing:view',
  MarketingEdit: 'marketing:edit',
  DataView: 'data:view',
  DataEdit: 'data:edit',
  FinanceView: 'finance:view',
  FinanceEdit: 'finance:edit',
  ProfitSharingView: 'profit_sharing:view',
  ProfitSharingManage: 'profit_sharing:manage',
  ProfitSharingExecute: 'profit_sharing:execute',
  MessageView: 'message:view',
  MessageEdit: 'message:edit',
  SystemView: 'system:view',
  SystemEdit: 'system:edit',
  SystemRoleView: 'role:view',
  SystemRoleEdit: 'role:edit',
} as const

export type PermissionCode = (typeof Permission)[keyof typeof Permission]

/**
 * 页面级权限码（「订单中心」「退款处理」这种具体页面），每个页面拆「查看」+「操作」两个动作。
 *
 * 与上面的动作级 `Permission`（view/edit）并存：动作级是历史遗留的「模块:动作」粗粒度，
 * 本常量是目标粒度「模块:页面:动作」的二维权限。
 * 后台「角色权限」的二级折叠树、路由 `meta.permissions` 均使用本常量。
 *
 * 命名约定：`模块:页面:view`（查看）/ `模块:页面:edit`（操作）。
 * 结构：每个页面一个对象，含 `view` 与 `edit` 两个权限码。
 */
export const PagePermission = {
  // 运营总览
  Dashboard: { view: 'dashboard:overview:view', edit: 'dashboard:overview:edit' },
  // 交易履约
  OrderCenter: { view: 'order:center:view', edit: 'order:center:edit' },
  OrderRefunds: { view: 'order:refunds:view', edit: 'order:refunds:edit' },
  OrderComplaints: { view: 'order:complaints:view', edit: 'order:complaints:edit' },
  // 门店管理
  StoreList: { view: 'store:list:view', edit: 'store:list:edit' },
  StorePrinters: { view: 'store:printers:view', edit: 'store:printers:edit' },
  // 商品中心
  ProductCategories: { view: 'product:categories:view', edit: 'product:categories:edit' },
  ProductList: { view: 'product:list:view', edit: 'product:list:edit' },
  ProductCombos: { view: 'product:combos:view', edit: 'product:combos:edit' },
  ProductExtras: { view: 'product:extras:view', edit: 'product:extras:edit' },
  // 配送与计价
  DeliveryMinAmount: { view: 'delivery:min-amount:view', edit: 'delivery:min-amount:edit' },
  DeliveryAreas: { view: 'delivery:areas:view', edit: 'delivery:areas:edit' },
  DeliveryPoints: { view: 'delivery:points:view', edit: 'delivery:points:edit' },
  // 无人机管理
  DroneDevices: { view: 'drone:devices:view', edit: 'drone:devices:edit' },
  DroneTasks: { view: 'drone:tasks:view', edit: 'drone:tasks:edit' },
  DroneFlights: { view: 'drone:flights:view', edit: 'drone:flights:edit' },
  DroneFaults: { view: 'drone:faults:view', edit: 'drone:faults:edit' },
  DroneMaintenances: { view: 'drone:maintenances:view', edit: 'drone:maintenances:edit' },
  // 用户管理
  UserList: { view: 'user:list:view', edit: 'user:list:edit' },
  // 会员中心
  MemberLevels: { view: 'member:levels:view', edit: 'member:levels:edit' },
  MemberRules: { view: 'member:rules:view', edit: 'member:rules:edit' },
  MemberBenefits: { view: 'member:benefits:view', edit: 'member:benefits:edit' },
  // 营销中心
  MarketingCoupons: { view: 'marketing:coupons:view', edit: 'marketing:coupons:edit' },
  MarketingPointsMall: { view: 'marketing:points-mall:view', edit: 'marketing:points-mall:edit' },
  MarketingCards: { view: 'marketing:cards:view', edit: 'marketing:cards:edit' },
  MarketingRecharge: { view: 'marketing:recharge:view', edit: 'marketing:recharge:edit' },
  MarketingBanners: { view: 'marketing:banners:view', edit: 'marketing:banners:edit' },
  MarketingGroupBuy: { view: 'marketing:group-buy:view', edit: 'marketing:group-buy:edit' },
  MarketingBargain: { view: 'marketing:bargain:view', edit: 'marketing:bargain:edit' },
  MarketingInvite: { view: 'marketing:invite:view', edit: 'marketing:invite:edit' },
  MarketingFlashSale: { view: 'marketing:flash-sale:view', edit: 'marketing:flash-sale:edit' },
  // 数据中心
  DataOverview: { view: 'data:overview:view', edit: 'data:overview:edit' },
  DataOrders: { view: 'data:orders:view', edit: 'data:orders:edit' },
  DataProducts: { view: 'data:products:view', edit: 'data:products:edit' },
  DataStoreRank: { view: 'data:store-rank:view', edit: 'data:store-rank:edit' },
  // 财务与支付
  FinanceTransactions: { view: 'finance:transactions:view', edit: 'finance:transactions:edit' },
  FinanceReconciliation: { view: 'finance:reconciliation:view', edit: 'finance:reconciliation:edit' },
  FinanceRefunds: { view: 'finance:refunds:view', edit: 'finance:refunds:edit' },
  FinanceProfitSharing: { view: 'finance:profit-sharing:view', edit: 'finance:profit-sharing:edit' },
  FinanceProfitSharingRules: { view: 'finance:profit-sharing-rules:view', edit: 'finance:profit-sharing-rules:edit' },
  FinanceProfitSharingReceivers: { view: 'finance:profit-sharing-receivers:view', edit: 'finance:profit-sharing-receivers:edit' },
  // 系统管理
  SystemAdmins: { view: 'system:admins:view', edit: 'system:admins:edit' },
  SystemRoles: { view: 'system:roles:view', edit: 'system:roles:edit' },
  SystemLogs: { view: 'system:logs:view', edit: 'system:logs:edit' },
  SystemParams: { view: 'system:params:view', edit: 'system:params:edit' },
  SystemMessages: { view: 'system:messages:view', edit: 'system:messages:edit' },
} as const

export type PagePermissionCode =
  (typeof PagePermission)[keyof typeof PagePermission]['view' | 'edit']
